import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { Alert, Avatar, Box, Button, Card, CardContent, Checkbox, CircularProgress, FormControlLabel, Switch, Typography } from '@mui/material';
import { useHistory } from 'react-router-dom';
import { IconPhoto, IconRefresh, IconSparkles, IconTrash, IconUpload } from '@tabler/icons-react';
import api from '../../../../services/api';

const formatSyncDate = (iso) => {
    if (!iso) return null;
    return new Date(iso).toLocaleString('pt-BR');
};

const ConfiguracoesConta = () => {
    const history = useHistory();
    const user = useSelector((state) => state.account.user);
    const [appId, setAppId] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [prefixarNomeAtendente, setPrefixarNomeAtendente] = useState(false);
    const [servicosHabilitado, setServicosHabilitado] = useState(false);
    const [savingServicos, setSavingServicos] = useState(false);
    const [status, setStatus] = useState({ type: '', message: '' });
    const [knowledgeStatus, setKnowledgeStatus] = useState(null);
    const [syncingKnowledge, setSyncingKnowledge] = useState(false);

    //? Google Agenda - um usuário pode conectar VÁRIAS contas Google (ex:
    //? Workspace pessoal + do trabalho) e ver a Agenda do CRM com todas
    //? juntas, por isso é uma lista, não uma conexão única.
    const [googleCalendarConnections, setGoogleCalendarConnections] = useState([]);
    const [loadingGoogleCalendar, setLoadingGoogleCalendar] = useState(true);
    const [savingGoogleCalendarConnectionId, setSavingGoogleCalendarConnectionId] = useState(null);
    const [disconnectingGoogleCalendarConnectionId, setDisconnectingGoogleCalendarConnectionId] = useState(null);

    //? Logo do App (usada no PDF de Contrato/Proposta e na marca do PWA)
    const [logoUrl, setLogoUrl] = useState(null);
    const [uploadingLogo, setUploadingLogo] = useState(false);
    const [removingLogo, setRemovingLogo] = useState(false);
    const logoInputRef = useRef(null);

    //? Papel timbrado (imagem de fundo do PDF de Contrato/Proposta) - por
    // CONTA, não por App (ver comentário em `core/models.py::Account.papel_timbrado`).
    const [papelTimbradoUrl, setPapelTimbradoUrl] = useState(null);
    const [uploadingPapelTimbrado, setUploadingPapelTimbrado] = useState(false);
    const [removingPapelTimbrado, setRemovingPapelTimbrado] = useState(false);
    const papelTimbradoInputRef = useRef(null);

    useEffect(() => {
        (async () => {
            try {
                const res = await api.get('v1/apps/default/');
                setAppId(res.data.app.id);
            } catch (err) {
                console.error(err);
                setStatus({ type: 'error', message: 'Erro ao carregar o app.' });
                setLoading(false);
            }
        })();
    }, []);

    const fetchAppLogo = async (currentAppId) => {
        try {
            const res = await api.get(`v1/apps/${currentAppId}/`);
            setLogoUrl(res.data?.app?.logo_url || null);
        } catch (err) {
            console.error(err);
        }
    };

    useEffect(() => {
        if (!appId) return;
        fetchAppLogo(appId);
    }, [appId]);

    const handleUploadLogo = async (e) => {
        const file = e.target.files?.[0];
        e.target.value = '';
        if (!file || !appId) return;

        setUploadingLogo(true);
        setStatus({ type: '', message: '' });
        try {
            const formData = new FormData();
            formData.append('logo', file);
            const res = await api.post(`v1/apps/${appId}/logo/`, formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            setLogoUrl(res.data.logo_url);
            setStatus({ type: 'success', message: 'Logo atualizada com sucesso!' });
        } catch (err) {
            console.error(err);
            setStatus({ type: 'error', message: err.response?.data?.error || 'Erro ao enviar a logo.' });
        } finally {
            setUploadingLogo(false);
        }
    };

    const handleRemoveLogo = async () => {
        if (!appId) return;
        setRemovingLogo(true);
        setStatus({ type: '', message: '' });
        try {
            await api.delete(`v1/apps/${appId}/logo/`);
            setLogoUrl(null);
            setStatus({ type: 'success', message: 'Logo removida.' });
        } catch (err) {
            console.error(err);
            setStatus({ type: 'error', message: 'Erro ao remover a logo.' });
        } finally {
            setRemovingLogo(false);
        }
    };

    const handleUploadPapelTimbrado = async (e) => {
        const file = e.target.files?.[0];
        e.target.value = '';
        if (!file || !appId) return;

        setUploadingPapelTimbrado(true);
        setStatus({ type: '', message: '' });
        try {
            const formData = new FormData();
            formData.append('papel_timbrado', file);
            const res = await api.post(`v1/apps/${appId}/papel_timbrado/`, formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            setPapelTimbradoUrl(res.data.papel_timbrado_url);
            setStatus({ type: 'success', message: 'Papel timbrado atualizado com sucesso!' });
        } catch (err) {
            console.error(err);
            setStatus({ type: 'error', message: err.response?.data?.error || 'Erro ao enviar o papel timbrado.' });
        } finally {
            setUploadingPapelTimbrado(false);
        }
    };

    const handleRemovePapelTimbrado = async () => {
        if (!appId) return;
        setRemovingPapelTimbrado(true);
        setStatus({ type: '', message: '' });
        try {
            await api.delete(`v1/apps/${appId}/papel_timbrado/`);
            setPapelTimbradoUrl(null);
            setStatus({ type: 'success', message: 'Papel timbrado removido.' });
        } catch (err) {
            console.error(err);
            setStatus({ type: 'error', message: 'Erro ao remover o papel timbrado.' });
        } finally {
            setRemovingPapelTimbrado(false);
        }
    };

    useEffect(() => {
        if (!appId) return;
        (async () => {
            try {
                const res = await api.get(`v1/apps/${appId}/account_settings/`);
                setPrefixarNomeAtendente(Boolean(res.data.prefixar_nome_atendente));
                setServicosHabilitado(Boolean(res.data.servicos_habilitado));
                setPapelTimbradoUrl(res.data.papel_timbrado_url || null);
            } catch (err) {
                console.error(err);
                setStatus({ type: 'error', message: 'Erro ao carregar os ajustes da conta.' });
            } finally {
                setLoading(false);
            }
        })();
    }, [appId]);

    const handleToggle = async (e) => {
        const novoValor = e.target.checked;
        setPrefixarNomeAtendente(novoValor);
        setSaving(true);
        setStatus({ type: '', message: '' });
        try {
            await api.patch(`v1/apps/${appId}/account_settings/`, { prefixar_nome_atendente: novoValor });
            setStatus({ type: 'success', message: 'Ajuste salvo!' });
        } catch (err) {
            console.error(err);
            setPrefixarNomeAtendente(!novoValor);
            setStatus({ type: 'error', message: 'Erro ao salvar o ajuste.' });
        } finally {
            setSaving(false);
        }
    };

    const isAdmin = user?.role === 'admin';

    const fetchGoogleCalendarStatus = async (currentAppId) => {
        try {
            const res = await api.get(`v1/apps/${currentAppId}/google_calendar/connections/`);
            setGoogleCalendarConnections(res.data || []);
        } catch (err) {
            console.error(err);
        } finally {
            setLoadingGoogleCalendar(false);
        }
    };

    useEffect(() => {
        if (!appId) return;
        fetchGoogleCalendarStatus(appId);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [appId]);

    const handleToggleGoogleCalendarSync = async (connectionId, e) => {
        const novoValor = e.target.checked;
        setSavingGoogleCalendarConnectionId(connectionId);
        try {
            await api.patch(`v1/apps/${appId}/google_calendar/connections/${connectionId}/`, { sync_enabled: novoValor });
            setGoogleCalendarConnections((prev) => prev.map((c) => (c.id === connectionId ? { ...c, sync_enabled: novoValor } : c)));
        } catch (err) {
            console.error(err);
            setStatus({ type: 'error', message: 'Erro ao atualizar a sincronização.' });
        } finally {
            setSavingGoogleCalendarConnectionId(null);
        }
    };

    const handleSetGoogleCalendarPrimary = async (connectionId) => {
        setSavingGoogleCalendarConnectionId(connectionId);
        try {
            await api.patch(`v1/apps/${appId}/google_calendar/connections/${connectionId}/`, { is_primary: true });
            setGoogleCalendarConnections((prev) => prev.map((c) => ({ ...c, is_primary: c.id === connectionId })));
            setStatus({ type: 'success', message: 'Conta padrão atualizada.' });
        } catch (err) {
            console.error(err);
            setStatus({ type: 'error', message: 'Erro ao definir a conta padrão.' });
        } finally {
            setSavingGoogleCalendarConnectionId(null);
        }
    };

    const handleDisconnectGoogleCalendar = async (connectionId) => {
        setDisconnectingGoogleCalendarConnectionId(connectionId);
        try {
            await api.delete(`v1/apps/${appId}/google_calendar/connections/${connectionId}/`);
            setGoogleCalendarConnections((prev) => prev.filter((c) => c.id !== connectionId));
            setStatus({ type: 'success', message: 'Conta Google desconectada.' });
        } catch (err) {
            console.error(err);
            setStatus({ type: 'error', message: 'Erro ao desconectar essa conta Google.' });
        } finally {
            setDisconnectingGoogleCalendarConnectionId(null);
        }
    };

    const fetchKnowledgeStatus = async (currentAppId) => {
        try {
            const res = await api.get(`v1/apps/${currentAppId}/copiloto/knowledge/`);
            setKnowledgeStatus(res.data);
        } catch (err) {
            console.error(err);
        }
    };

    useEffect(() => {
        if (!appId || !isAdmin) return;
        fetchKnowledgeStatus(appId);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [appId, isAdmin]);

    const handleSyncKnowledge = async () => {
        setSyncingKnowledge(true);
        setStatus({ type: '', message: '' });
        try {
            await api.post(`v1/apps/${appId}/copiloto/knowledge/`);
            setStatus({ type: 'success', message: 'Sincronização iniciada - pode levar alguns minutos até o Assistente IA usar o conteúdo atualizado.' });
            await fetchKnowledgeStatus(appId);
        } catch (err) {
            console.error(err);
            setStatus({ type: 'error', message: err.response?.data?.error || 'Erro ao sincronizar a base de conhecimento.' });
        } finally {
            setSyncingKnowledge(false);
        }
    };

    const handleToggleServicos = async (e) => {
        const novoValor = e.target.checked;
        setServicosHabilitado(novoValor);
        setSavingServicos(true);
        setStatus({ type: '', message: '' });
        try {
            await api.patch(`v1/apps/${appId}/account_settings/`, { servicos_habilitado: novoValor });
            setStatus({ type: 'success', message: 'Ajuste salvo!' });
        } catch (err) {
            console.error(err);
            setServicosHabilitado(!novoValor);
            setStatus({ type: 'error', message: 'Erro ao salvar o ajuste.' });
        } finally {
            setSavingServicos(false);
        }
    };

    return (
        <Box p={4}>
            <Typography variant="h4" fontWeight="700" mb={3}>
                Ajustes da Conta
            </Typography>

            {status.message && (
                <Alert severity={status.type} sx={{ mb: 2 }}>
                    {status.message}
                </Alert>
            )}

            <Card sx={{ borderRadius: 3, maxWidth: 600 }}>
                <CardContent>
                    <Typography variant="h6" fontWeight="700" mb={1}>
                        Mensagens do chat
                    </Typography>

                    {loading ? (
                        <CircularProgress size={24} />
                    ) : (
                        <FormControlLabel
                            control={<Checkbox checked={prefixarNomeAtendente} disabled={saving} onChange={handleToggle} />}
                            label="Mostrar o nome do atendente antes da mensagem enviada ao cliente"
                        />
                    )}
                    <Typography variant="body2" color="text.secondary" mt={1}>
                        Quando ligado, uma mensagem enviada por um atendente humano no chat vai com o nome dele antes do texto.
                    </Typography>
                </CardContent>
            </Card>

            <Card sx={{ borderRadius: 3, maxWidth: 600, mt: 3 }}>
                <CardContent>
                    <Typography variant="h6" fontWeight="700" mb={1}>
                        Módulos
                    </Typography>

                    {loading ? (
                        <CircularProgress size={24} />
                    ) : (
                        <FormControlLabel
                            control={<Checkbox checked={servicosHabilitado} disabled={savingServicos} onChange={handleToggleServicos} />}
                            label="Habilitar módulo de Serviços"
                        />
                    )}
                    <Typography variant="body2" color="text.secondary" mt={1}>
                        Libera o catálogo de serviços (avulsos ou recorrentes) e os contratos de clientes no menu do CRM.
                    </Typography>
                </CardContent>
            </Card>

            <Card sx={{ borderRadius: 3, maxWidth: 600, mt: 3 }}>
                <CardContent>
                    <Typography variant="h6" fontWeight="700" mb={1}>
                        Documentos
                    </Typography>
                    <Typography variant="body2" color="text.secondary" mb={2}>
                        Configure os campos personalizados de Contrato de Serviço e Proposta - usados no formulário de
                        criação/edição desses documentos, inclusive na exportação em PDF.
                    </Typography>
                    <Box display="flex" gap={2}>
                        <Button variant="outlined" onClick={() => history.push('/dashboard/config/custom-fields?tab=contrato')}>
                            Incluir contrato
                        </Button>
                        <Button variant="outlined" onClick={() => history.push('/dashboard/config/custom-fields?tab=proposta')}>
                            Incluir proposta
                        </Button>
                    </Box>
                </CardContent>
            </Card>

            <Card sx={{ borderRadius: 3, maxWidth: 600, mt: 3 }}>
                <CardContent>
                    <Typography variant="h6" fontWeight="700" mb={1}>
                        Logo da empresa
                    </Typography>
                    <Typography variant="body2" color="text.secondary" mb={2}>
                        Aparece no canto superior esquerdo do PDF de Contrato de Serviço, no canto superior direito da
                        Proposta, e como ícone do app quando alguém instalar o CRM na tela inicial do celular.
                    </Typography>

                    <Box display="flex" alignItems="center" gap={2}>
                        <Avatar
                            variant="rounded"
                            src={logoUrl || undefined}
                            sx={{ width: 72, height: 72, bgcolor: 'action.hover' }}
                        >
                            <IconPhoto size={28} />
                        </Avatar>

                        <Box display="flex" flexDirection="column" gap={1}>
                            <input
                                type="file"
                                accept="image/*"
                                hidden
                                ref={logoInputRef}
                                onChange={handleUploadLogo}
                            />
                            <Button
                                variant="outlined"
                                size="small"
                                startIcon={uploadingLogo ? <CircularProgress size={14} /> : <IconUpload size={16} />}
                                onClick={() => logoInputRef.current?.click()}
                                disabled={uploadingLogo || removingLogo}
                                sx={{ textTransform: 'none' }}
                            >
                                {logoUrl ? 'Trocar logo' : 'Enviar logo'}
                            </Button>
                            {logoUrl && (
                                <Button
                                    variant="text"
                                    color="error"
                                    size="small"
                                    startIcon={<IconTrash size={16} />}
                                    onClick={handleRemoveLogo}
                                    disabled={uploadingLogo || removingLogo}
                                    sx={{ textTransform: 'none' }}
                                >
                                    Remover
                                </Button>
                            )}
                        </Box>
                    </Box>
                </CardContent>
            </Card>

            <Card sx={{ borderRadius: 3, maxWidth: 600, mt: 3 }}>
                <CardContent>
                    <Typography variant="h6" fontWeight="700" mb={1}>
                        Papel timbrado
                    </Typography>
                    <Typography variant="body2" color="text.secondary" mb={2}>
                        Imagem usada como fundo da página inteira no PDF de Contrato de Serviço e Proposta - vale pra
                        conta toda (não muda por app). Use uma imagem clara/sutil pra não atrapalhar a leitura do texto
                        por cima.
                    </Typography>

                    <Box display="flex" alignItems="center" gap={2}>
                        <Avatar
                            variant="rounded"
                            src={papelTimbradoUrl || undefined}
                            sx={{ width: 72, height: 96, bgcolor: 'action.hover' }}
                        >
                            <IconPhoto size={28} />
                        </Avatar>

                        <Box display="flex" flexDirection="column" gap={1}>
                            <input
                                type="file"
                                accept="image/*"
                                hidden
                                ref={papelTimbradoInputRef}
                                onChange={handleUploadPapelTimbrado}
                            />
                            <Button
                                variant="outlined"
                                size="small"
                                startIcon={uploadingPapelTimbrado ? <CircularProgress size={14} /> : <IconUpload size={16} />}
                                onClick={() => papelTimbradoInputRef.current?.click()}
                                disabled={uploadingPapelTimbrado || removingPapelTimbrado}
                                sx={{ textTransform: 'none' }}
                            >
                                {papelTimbradoUrl ? 'Trocar papel timbrado' : 'Enviar papel timbrado'}
                            </Button>
                            {papelTimbradoUrl && (
                                <Button
                                    variant="text"
                                    color="error"
                                    size="small"
                                    startIcon={<IconTrash size={16} />}
                                    onClick={handleRemovePapelTimbrado}
                                    disabled={uploadingPapelTimbrado || removingPapelTimbrado}
                                    sx={{ textTransform: 'none' }}
                                >
                                    Remover
                                </Button>
                            )}
                        </Box>
                    </Box>
                </CardContent>
            </Card>

            <Card sx={{ borderRadius: 3, maxWidth: 600, mt: 3 }}>
                <CardContent>
                    <Typography variant="h6" fontWeight="700" mb={1}>
                        Google Agenda
                    </Typography>
                    <Typography variant="body2" color="text.secondary" mb={2}>
                        {isAdmin
                            ? 'Contas Google vinculadas a você. Pra conectar novas contas ou atribuí-las a outros usuários, use Configurações > Canais > Google Agenda.'
                            : 'Contas Google que um administrador vinculou a você - os eventos delas sincronizam nos dois sentidos com a Agenda do CRM.'}
                    </Typography>

                    {loadingGoogleCalendar ? (
                        <CircularProgress size={24} />
                    ) : (
                        <Box display="flex" flexDirection="column" gap={2} alignItems="flex-start" width="100%">
                            {googleCalendarConnections.map((connection) => (
                                <Box
                                    key={connection.id}
                                    sx={{
                                        width: '100%',
                                        p: 2,
                                        borderRadius: 2,
                                        border: '1px solid rgba(0,0,0,0.12)',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        gap: 0.5
                                    }}
                                >
                                    <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                                        <Typography variant="body2">
                                            <b>{connection.google_email}</b>
                                        </Typography>
                                        {connection.user?.id !== user?.id && (
                                            <Typography variant="caption" color="text.secondary">
                                                (de {connection.user?.name})
                                            </Typography>
                                        )}
                                        {connection.is_primary && (
                                            <Typography
                                                variant="caption"
                                                sx={{ px: 1, py: 0.2, borderRadius: 1, bgcolor: 'primary.main', color: 'primary.contrastText' }}
                                            >
                                                Padrão
                                            </Typography>
                                        )}
                                    </Box>
                                    <Typography variant="caption" color="text.secondary">
                                        {connection.last_synced_at
                                            ? `Última sincronização: ${formatSyncDate(connection.last_synced_at)}`
                                            : 'Ainda não sincronizou (roda a cada 10 minutos)'}
                                    </Typography>
                                    <Box display="flex" alignItems="center" gap={1} flexWrap="wrap" mt={0.5}>
                                        <FormControlLabel
                                            control={
                                                <Switch
                                                    checked={Boolean(connection.sync_enabled)}
                                                    disabled={savingGoogleCalendarConnectionId === connection.id}
                                                    onChange={(e) => handleToggleGoogleCalendarSync(connection.id, e)}
                                                />
                                            }
                                            label="Sincronização ativa"
                                        />
                                        {!connection.is_primary && (
                                            <Button
                                                size="small"
                                                disabled={savingGoogleCalendarConnectionId === connection.id}
                                                onClick={() => handleSetGoogleCalendarPrimary(connection.id)}
                                                sx={{ textTransform: 'none' }}
                                            >
                                                Tornar padrão
                                            </Button>
                                        )}
                                        <Button
                                            variant="outlined"
                                            color="error"
                                            size="small"
                                            onClick={() => handleDisconnectGoogleCalendar(connection.id)}
                                            disabled={disconnectingGoogleCalendarConnectionId === connection.id}
                                            sx={{ textTransform: 'none' }}
                                        >
                                            {disconnectingGoogleCalendarConnectionId === connection.id ? 'Desconectando...' : 'Desconectar'}
                                        </Button>
                                    </Box>
                                </Box>
                            ))}

                            {googleCalendarConnections.length === 0 && (
                                <Typography variant="body2" color="text.secondary">
                                    Nenhuma conta Google vinculada a você ainda.
                                </Typography>
                            )}

                            <Typography variant="caption" color="text.secondary">
                                Pra conectar uma conta nova ou mudar quem vê o quê, peça a um administrador (Configurações &gt; Canais
                                &gt; Google Agenda).
                            </Typography>
                        </Box>
                    )}
                </CardContent>
            </Card>

            {isAdmin && (
                <Card sx={{ borderRadius: 3, maxWidth: 600, mt: 3, overflow: 'hidden', position: 'relative' }}>
                    <Box
                        sx={{
                            position: 'absolute',
                            inset: 0,
                            background:
                                'radial-gradient(circle at 0% 0%, rgba(99,102,241,0.08), transparent 55%), radial-gradient(circle at 100% 0%, rgba(6,182,212,0.06), transparent 45%)',
                            pointerEvents: 'none'
                        }}
                    />
                    <CardContent sx={{ position: 'relative' }}>
                        <Box display="flex" alignItems="center" gap={1} mb={1}>
                            <Box
                                sx={{
                                    width: 26,
                                    height: 26,
                                    borderRadius: '8px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    background: 'linear-gradient(135deg, #6366f1 0%, #a855f7 55%, #06b6d4 100%)'
                                }}
                            >
                                <IconSparkles size={15} color="#fff" />
                            </Box>
                            <Typography variant="h6" fontWeight="700">
                                LOGED AI ✨ - Base de conhecimento
                            </Typography>
                        </Box>
                        <Typography variant="body2" color="text.secondary" mb={2}>
                            O Assistente IA usa a documentação do sistema pra responder com precisão. Sempre que uma
                            atualização mudar como algo funciona no CRM, sincronize novamente pra ele aprender o que mudou.
                        </Typography>
                        <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
                            <Button
                                variant="contained"
                                onClick={handleSyncKnowledge}
                                disabled={syncingKnowledge || !appId}
                                startIcon={syncingKnowledge ? <CircularProgress size={16} color="inherit" /> : <IconRefresh size={17} />}
                                sx={{
                                    borderRadius: '999px',
                                    textTransform: 'none',
                                    fontWeight: 700,
                                    background: 'linear-gradient(135deg, #6366f1 0%, #a855f7 55%, #06b6d4 100%)',
                                    '&:hover': { background: 'linear-gradient(135deg, #4f46e5 0%, #9333ea 55%, #0891b2 100%)' }
                                }}
                            >
                                {syncingKnowledge ? 'Sincronizando...' : 'Sincronizar conhecimento do Assistente'}
                            </Button>
                            <Typography variant="caption" color="text.secondary">
                                {knowledgeStatus?.synced_at
                                    ? `Última sincronização: ${formatSyncDate(knowledgeStatus.synced_at)} (${knowledgeStatus.chunks_count} trechos)`
                                    : 'Ainda não sincronizado'}
                            </Typography>
                        </Box>
                    </CardContent>
                </Card>
            )}
        </Box>
    );
};

export default ConfiguracoesConta;
